/**
 * What happens outside the window: the badge, and notifications.
 *
 * Both are driven by one thing — the unread counts the sync store already
 * keeps — rather than by the message stream. That is deliberate. The counts
 * are what the server says, they survive a reconnect and a reload, and they
 * are the same numbers the sidebar is showing, so the badge and the sidebar
 * can never disagree.
 *
 * It also means a notification never carries a message body. That is a
 * property worth keeping: a preview on a locked screen or a shared desk is a
 * leak that the person being notified did not choose, and relaii's own push
 * gateway takes the same position (`protocol/push-v1.md`).
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { useSyncStore } from '@/api/store'
import { unreadOf } from '@/api/types'
import type { NotificationPreference, Room } from '@/api/types'
import { useRoomLabels, roomLabel } from '@/components/Sidebar'
import { focusDesktopWindow, reportUnread } from '@/lib/desktop'

interface Reading {
  unread: number
  mentions: number
}

/** Whether an arrival in this room is worth interrupting somebody for. */
function wanted(
  room: Room,
  preference: NotificationPreference,
  mentioned: boolean,
): boolean {
  const membership = room.my_membership
  if (membership === null) return false

  const override = membership.notification ?? 'default'
  if (override === 'muted' || membership.muted) return mentioned && override === 'all'
  if (override === 'mentions') return mentioned
  if (override === 'all') return true

  switch (preference) {
    case 'nothing':
      return false
    case 'mentions':
      return mentioned
    case 'dm_and_mentions':
      return mentioned || room.kind !== 'channel'
    default:
      return true
  }
}

function canNotify(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

/**
 * Only when the window is not the one being looked at. Notifying somebody
 * about a message they can already see is how a client teaches people to turn
 * notifications off.
 */
function unattended(): boolean {
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'hidden' || !document.hasFocus()
}

export function useActivityBridge(): void {
  const { rooms } = useSyncStore()
  const { user, server } = useAuth()
  const labels = useRoomLabels()
  const navigate = useNavigate()

  const previous = useRef<Map<string, Reading> | null>(null)
  // Read inside the effect without making it a dependency: a new label map
  // arrives on every room the sidebar draws, and re-running this then would
  // compare a snapshot against itself.
  const labelsRef = useRef(labels)
  labelsRef.current = labels

  const preference: NotificationPreference = user?.notification_preference ?? 'all'
  const spaceName = server?.display_name?.trim() || server?.server_id || 'relaii'

  // AppShell is the only mounted owner of the browser title. Restore the
  // neutral product name when it unmounts at sign-out.
  useEffect(() => {
    return () => {
      document.title = 'relaii'
    }
  }, [])

  useEffect(() => {
    const current = new Map<string, Reading>()
    let unread = 0
    let mentions = 0

    for (const room of rooms) {
      const state = unreadOf(room)
      current.set(room.id, { unread: state.count, mentions: state.mentions })
      if (room.my_membership?.muted === true) continue
      unread += state.count
      mentions += state.mentions
    }

    reportUnread({ unread, mentions })
    document.title = `${unread > 0 ? `(${unread}) ` : ''}${spaceName} - relaii`

    const before = previous.current
    previous.current = current

    // The first pass is the backlog, not an arrival: notifying about it would
    // fire a dozen notifications on every reload.
    if (before === null || !canNotify() || !unattended()) return

    for (const room of rooms) {
      const seen = before.get(room.id)
      if (seen === undefined) continue

      const now = current.get(room.id)
      if (now === undefined) continue

      const newMentions = now.mentions > seen.mentions
      const newMessages = now.unread > seen.unread
      if (!newMentions && !newMessages) continue
      if (!wanted(room, preference, newMentions)) continue

      const name = roomLabel(room, labelsRef.current)
      const where = room.kind === 'channel' ? `#${name}` : name

      const notification = new Notification(
        newMentions ? `You were mentioned in ${where}` : where,
        {
          body: newMentions ? 'Open relaii to read it.' : 'New messages.',
          // One notification per room replaces the last, rather than stacking
          // twelve of them for one busy channel.
          tag: `relaii-room-${room.id}`,
          silent: false,
        },
      )

      notification.onclick = () => {
        focusDesktopWindow()
        window.focus()
        navigate(`/rooms/${encodeURIComponent(room.id)}`)
        notification.close()
      }
    }
  }, [navigate, preference, rooms, spaceName])
}
